/**
 * Вопрос клиента (client-questions; ТЗ п.9.7): переписка с Центрикой, уточнение с вложениями и
 * «Закрыть вопрос». Открытие вопроса снимает отметку о новом ответе (точку на конвертике).
 */
import { useEffect, useState } from 'react'
import { ArrowLeft } from 'lucide-react'
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '@/app/auth-context'
import { questionsApi, type QuestionDetail } from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import { PageHead } from '@/design-system/components/layout/page-head'
import { Card } from '@/design-system/components/layout/card'
import { Button } from '@/design-system/components/primitives/button'
import { ConfirmDialog } from '@/design-system/components/primitives/confirm-dialog'
import { Alert } from '@/design-system/components/feedback/alert'
import {
  QuestionReplyForm,
  QuestionStatusBadge,
  QuestionThread,
  formatDateTime,
} from '@/questions/question-ui'

export function QuestionPage() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { refresh } = useAuth()
  const [question, setQuestion] = useState<QuestionDetail | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [closing, setClosing] = useState(false)
  // Закрытие идёт: повторный щелчок не отправляет второй запрос (он ответил бы «уже закрыт»).
  const [closeBusy, setCloseBusy] = useState(false)

  useEffect(() => {
    if (!id) return
    const controller = new AbortController()
    setQuestion(null)
    setError(null)
    questionsApi
      .get(id, controller.signal)
      .then((q) => {
        if (controller.signal.aborted) return
        setQuestion(q)
        // Ответ прочитан — точка на конвертике и в списке гаснет.
        void refresh()
      })
      .catch((err) => {
        if (!controller.signal.aborted) setError(getErrorMessage(err, 'Не удалось загрузить вопрос'))
      })
    return () => controller.abort()
    // refresh — стабильная функция контекста; перечитываем только при смене вопроса.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  async function close() {
    if (!question || closeBusy) return
    setCloseBusy(true)
    try {
      setQuestion(await questionsApi.close(question.id))
      setClosing(false)
    } catch (err) {
      setClosing(false)
      setError(getErrorMessage(err, 'Не удалось закрыть вопрос'))
    } finally {
      setCloseBusy(false)
    }
  }

  return (
    <div>
      <div className="mb-4">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => navigate('/questions')}
          leftIcon={<ArrowLeft className="size-4 stroke-current" aria-hidden />}
        >
          К моим вопросам
        </Button>
      </div>
      {error && !question ? (
        <Alert variant="danger">{error}</Alert>
      ) : !question ? (
        <p className="py-10 text-center text-txt-secondary">Загрузка…</p>
      ) : (
        <>
          <PageHead
            className="mb-4 flex-wrap"
            title={
              <span className="flex flex-wrap items-center gap-2.5">
                <span className="break-words">{question.subject}</span>
                <QuestionStatusBadge status={question.status} side="client" />
              </span>
            }
            subtitle={
              <>
                Вопрос № {question.number} от {formatDateTime(question.createdAt)}
                {question.orderId ? (
                  <>
                    {' · '}
                    <RouterLink to={`/orders/${question.orderId}`} className="text-accent hover:underline">
                      заказ № {question.orderNumber}
                    </RouterLink>
                  </>
                ) : null}
              </>
            }
            actions={
              question.canClose ? (
                <Button variant="secondary" onClick={() => setClosing(true)}>
                  Закрыть вопрос
                </Button>
              ) : undefined
            }
          />
          {error ? (
            <Alert variant="danger" className="mb-4">
              {error}
            </Alert>
          ) : null}
          <QuestionThread question={question} side="client" />
          {question.canReply ? (
            <Card header="Дополнить вопрос" className="mt-4">
              <QuestionReplyForm
                label="Уточнение"
                submitLabel="Отправить"
                placeholder="Если нужно что-то добавить — напишите здесь"
                send={(body, files) => questionsApi.reply(question.id, body, files)}
                onSent={setQuestion}
              />
            </Card>
          ) : (
            <Alert variant="info" className="mt-4">
              Вопрос закрыт. Если появится новый вопрос — задайте его в разделе «Мои вопросы».
            </Alert>
          )}
          <ConfirmDialog
            open={closing}
            title="Закрыть вопрос?"
            message="Вопрос закрывают, когда ответ получен. Дописать в закрытый вопрос будет нельзя."
            buttons={[
              {
                label: closeBusy ? 'Закрываем…' : 'Закрыть вопрос',
                variant: 'primary',
                disabled: closeBusy,
                onClick: () => void close(),
              },
              {
                label: 'Не закрывать',
                variant: 'secondary',
                disabled: closeBusy,
                onClick: () => setClosing(false),
              },
            ]}
            closeDisabled={closeBusy}
            onClose={() => setClosing(false)}
          />
        </>
      )}
    </div>
  )
}
